@import '../../utils/button-base.css';

:host {
  --ds-button-filled-bg: var(--color-background-bold-brand);
  --ds-button-filled-fg: var(--color-foreground-faint-brand);
  --ds-button-filled-border: var(--color-border-secondary);
}

.button-filled {
  background-color: var(--ds-button-filled-bg);
  color: var(--ds-button-filled-fg);
  transition:
    background-color var(--effect-motion-short-2),
    color var(--effect-motion-short-2);
  /* Hover/press: `.ds-interaction-fill` + contrast → `--on-*` (token overlay, not color-mix). */
}

/*
 * An open popup is a transient pressed state, not a selected state. ButtonFilled
 * deliberately has no active state, so hold the pressed wash for the popup's
 * complete rendered lifecycle instead of promoting any selected treatment.
 */
/* Inset stroke via interaction-fill ::after (above hover and pressed washes). */
.button-filled--bordered {
  --ds-interaction-border-color: var(--ds-button-filled-border);
}

/*
 * Parent-surface context affects only the optional inset border.
 * Intent + contrast continue to own fill, foreground, and interaction overlays.
 */
.button-filled--background-medium {
  --ds-button-filled-border: var(--color-border-on-medium-background-secondary);
}

.button-filled--background-bold {
  --ds-button-filled-border: var(--color-border-on-bold-background-secondary);
}

.button-filled--background-strong {
  --ds-button-filled-border: var(--color-border-on-strong-background-secondary);
}

.button-filled--background-translucent {
  --ds-button-filled-border: var(--color-translucent-border-secondary);
}

.button-filled--background-inverted {
  --ds-button-filled-border: var(--color-inverted-border-secondary);
}

.button-filled--background-media {
  --ds-button-filled-border: var(--color-media-border-secondary);
}

.button-filled--background-always-dark {
  --ds-button-filled-border: var(--color-always-dark-border-secondary);
}

/* Inactive: `.ds-control-inactive` (50% opacity). */

/* ── Intent + contrast (bold default) ─────────────────────────────────────── */

.button-filled--intent-neutral {
  --ds-button-filled-bg: var(--color-background-bold-neutral);
  --ds-button-filled-fg: var(--color-foreground-faint-neutral);
}

.button-filled--intent-brand {
  --ds-button-filled-bg: var(--color-background-bold-brand);
  --ds-button-filled-fg: var(--color-foreground-faint-brand);
}

.button-filled--intent-ai {
  --ds-button-filled-bg: var(--color-background-bold-ai);
  --ds-button-filled-fg: var(--color-foreground-faint-ai);
}

.button-filled--intent-negative {
  --ds-button-filled-bg: var(--color-background-bold-negative);
  --ds-button-filled-fg: var(--color-foreground-faint-negative);
}

.button-filled--intent-warning {
  --ds-button-filled-bg: var(--color-background-bold-warning);
  --ds-button-filled-fg: var(--color-foreground-faint-warning);
}

.button-filled--intent-caution {
  --ds-button-filled-bg: var(--color-background-bold-caution);
  --ds-button-filled-fg: var(--color-foreground-faint-caution);
}

.button-filled--intent-positive {
  --ds-button-filled-bg: var(--color-background-bold-positive);
  --ds-button-filled-fg: var(--color-foreground-faint-positive);
}

.button-filled--intent-guide {
  --ds-button-filled-bg: var(--color-background-bold-guide);
  --ds-button-filled-fg: var(--color-foreground-faint-guide);
}

.button-filled--intent-walkthrough {
  --ds-button-filled-bg: var(--color-background-bold-walkthrough);
  --ds-button-filled-fg: var(--color-foreground-faint-walkthrough);
}

/* strong → medium fg */
.button-filled--contrast-strong.button-filled--intent-neutral {
  --ds-button-filled-bg: var(--color-background-strong-neutral);
  --ds-button-filled-fg: var(--color-foreground-medium-neutral);
}

.button-filled--contrast-strong.button-filled--intent-brand {
  --ds-button-filled-bg: var(--color-background-strong-brand);
  --ds-button-filled-fg: var(--color-foreground-medium-brand);
}

.button-filled--contrast-strong.button-filled--intent-ai {
  --ds-button-filled-bg: var(--color-background-strong-ai);
  --ds-button-filled-fg: var(--color-foreground-medium-ai);
}

.button-filled--contrast-strong.button-filled--intent-negative {
  --ds-button-filled-bg: var(--color-background-strong-negative);
  --ds-button-filled-fg: var(--color-foreground-medium-negative);
}

.button-filled--contrast-strong.button-filled--intent-warning {
  --ds-button-filled-bg: var(--color-background-strong-warning);
  --ds-button-filled-fg: var(--color-foreground-medium-warning);
}

.button-filled--contrast-strong.button-filled--intent-caution {
  --ds-button-filled-bg: var(--color-background-strong-caution);
  --ds-button-filled-fg: var(--color-foreground-medium-caution);
}

.button-filled--contrast-strong.button-filled--intent-positive {
  --ds-button-filled-bg: var(--color-background-strong-positive);
  --ds-button-filled-fg: var(--color-foreground-medium-positive);
}

.button-filled--contrast-strong.button-filled--intent-guide {
  --ds-button-filled-bg: var(--color-background-strong-guide);
  --ds-button-filled-fg: var(--color-foreground-medium-guide);
}

.button-filled--contrast-strong.button-filled--intent-walkthrough {
  --ds-button-filled-bg: var(--color-background-strong-walkthrough);
  --ds-button-filled-fg: var(--color-foreground-medium-walkthrough);
}

/* medium → strong fg */
.button-filled--contrast-medium.button-filled--intent-neutral {
  --ds-button-filled-bg: var(--color-background-medium-neutral);
  --ds-button-filled-fg: var(--color-foreground-strong-neutral);
}

.button-filled--contrast-medium.button-filled--intent-brand {
  --ds-button-filled-bg: var(--color-background-medium-brand);
  --ds-button-filled-fg: var(--color-foreground-strong-brand);
}

.button-filled--contrast-medium.button-filled--intent-ai {
  --ds-button-filled-bg: var(--color-background-medium-ai);
  --ds-button-filled-fg: var(--color-foreground-strong-ai);
}

.button-filled--contrast-medium.button-filled--intent-negative {
  --ds-button-filled-bg: var(--color-background-medium-negative);
  --ds-button-filled-fg: var(--color-foreground-strong-negative);
}

.button-filled--contrast-medium.button-filled--intent-warning {
  --ds-button-filled-bg: var(--color-background-medium-warning);
  --ds-button-filled-fg: var(--color-foreground-strong-warning);
}

.button-filled--contrast-medium.button-filled--intent-caution {
  --ds-button-filled-bg: var(--color-background-medium-caution);
  --ds-button-filled-fg: var(--color-foreground-strong-caution);
}

.button-filled--contrast-medium.button-filled--intent-positive {
  --ds-button-filled-bg: var(--color-background-medium-positive);
  --ds-button-filled-fg: var(--color-foreground-strong-positive);
}

.button-filled--contrast-medium.button-filled--intent-guide {
  --ds-button-filled-bg: var(--color-background-medium-guide);
  --ds-button-filled-fg: var(--color-foreground-strong-guide);
}

.button-filled--contrast-medium.button-filled--intent-walkthrough {
  --ds-button-filled-bg: var(--color-background-medium-walkthrough);
  --ds-button-filled-fg: var(--color-foreground-strong-walkthrough);
}

/* faint → bold fg */
.button-filled--contrast-faint.button-filled--intent-neutral {
  --ds-button-filled-bg: var(--color-background-faint-neutral);
  --ds-button-filled-fg: var(--color-foreground-bold-neutral);
}

.button-filled--contrast-faint.button-filled--intent-brand {
  --ds-button-filled-bg: var(--color-background-faint-brand);
  --ds-button-filled-fg: var(--color-foreground-bold-brand);
}

.button-filled--contrast-faint.button-filled--intent-ai {
  --ds-button-filled-bg: var(--color-background-faint-ai);
  --ds-button-filled-fg: var(--color-foreground-bold-ai);
}

.button-filled--contrast-faint.button-filled--intent-negative {
  --ds-button-filled-bg: var(--color-background-faint-negative);
  --ds-button-filled-fg: var(--color-foreground-bold-negative);
}

.button-filled--contrast-faint.button-filled--intent-warning {
  --ds-button-filled-bg: var(--color-background-faint-warning);
  --ds-button-filled-fg: var(--color-foreground-bold-warning);
}

.button-filled--contrast-faint.button-filled--intent-caution {
  --ds-button-filled-bg: var(--color-background-faint-caution);
  --ds-button-filled-fg: var(--color-foreground-bold-caution);
}

.button-filled--contrast-faint.button-filled--intent-positive {
  --ds-button-filled-bg: var(--color-background-faint-positive);
  --ds-button-filled-fg: var(--color-foreground-bold-positive);
}

.button-filled--contrast-faint.button-filled--intent-guide {
  --ds-button-filled-bg: var(--color-background-faint-guide);
  --ds-button-filled-fg: var(--color-foreground-bold-guide);
}

.button-filled--contrast-faint.button-filled--intent-walkthrough {
  --ds-button-filled-bg: var(--color-background-faint-walkthrough);
  --ds-button-filled-fg: var(--color-foreground-bold-walkthrough);
}

/*
 * Bold, strong, and medium fills already communicate semantic intent. Their
 * on-background foreground tokens preserve AA text contrast across themes,
 * including brand pairs whose chromatic counterparts sit on the 4.5:1 edge.
 */
.button-filled--contrast-bold.button-filled {
  --ds-button-filled-fg: var(--color-foreground-on-bold-background-primary);
}

.button-filled--contrast-strong.button-filled {
  --ds-button-filled-fg: var(--color-foreground-on-strong-background-primary);
}

.button-filled--contrast-medium.button-filled {
  --ds-button-filled-fg: var(--color-foreground-on-medium-background-primary);
}
